/**
 * The Cashflow forecast tab of an account (TMMAPP-0102 §7; TMMAPP-0101 moves revolving and loan accounts
 * onto it from `CashflowPanel`): the shared `ForecastCard` fed by the account's one projection (MM-D-53).
 * A cash account's KPIs are its Unallocated figures; a revolving or loan account has no allocations, so its
 * KPIs and list are its own balance, in its own currency. The chart reads the full horizon; the "Planned events"
 * list reads the same response, or the same endpoint with a narrower `from` / `to` after a
 * drill-down (never recomputed here). The envelope filter (D1) sits in the card's filter slot and
 * narrows the events list only. Unallocated balances are booked in the operating currency
 * (MM-D-63).
 */
import { useMemo, useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import type { EventView } from '@/api/projections';
import { ErrorState } from '@/components/ErrorState';
import { MoneyText } from '@/components/MoneyText';
import { ForecastCard, type ForecastKpi } from '@/features/cashflow/ForecastCard';
import { HORIZON_SCHEMA } from '@/features/cashflow/horizon';
import { scopeKey, type ChartSeries, type DrillDown } from '@/features/cashflow/types';
import { useOccurrenceHost } from '@/features/cashflow/useOccurrenceHost';
import { useProjection } from '@/features/cashflow/useProjection';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useUrlState } from '@/shared/useUrlState';
import { resolveTokens } from '@/theme/theme';
import { useOwnedEnvelopes } from './useOwnedEnvelopes';

interface AccountForecastTabProps {
  account: Account;
  /** The operating currency: the unallocated line and the allocation columns are in it. */
  operating: string | null;
  /** The account's own (bank) currency. */
  bank: string;
  owner: boolean;
  /** The envelope filter (0 = all envelopes); the page owns it in the URL. */
  envelopeId: number;
  onEnvelopeChange: (id: number) => void;
  reloadToken: number;
  onNotify: (message: string) => void;
}

export function AccountForecastTab({
  account,
  operating,
  bank,
  owner,
  envelopeId,
  onEnvelopeChange,
  reloadToken,
  onNotify,
}: AccountForecastTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const [{ h: horizon }] = useUrlState(HORIZON_SCHEMA);
  const { envelopes } = useOwnedEnvelopes();
  const scope = useMemo(() => ({ kind: 'account' as const, id: account.id }), [account.id]);
  const key = scopeKey(scope);
  const [drill, setDrill] = useState<DrillDown | null>(null);
  const [prev, setPrev] = useState({ key, horizon });
  if (prev.key !== key || prev.horizon !== horizon) {
    setPrev({ key, horizon });
    if (drill !== null) setDrill(null);
  }
  const { status, error, full, table, fromToday, reload } = useProjection(scope, horizon, drill);
  const host = useOccurrenceHost({ account, currency: bank, onChanged: reload, onNotify });
  const [lastReload, setLastReload] = useState(reloadToken);
  if (lastReload !== reloadToken) {
    setLastReload(reloadToken);
    void reload();
  }

  const cash = account.type === 'CASH';
  const foreign = bank !== operating;
  const chartSeries = useMemo<ChartSeries[]>(() => {
    if (full === null) return [];
    if (!cash) {
      const own = full.chartSeries.find((s) => s.id === 'balance');
      return own !== undefined ? [{ ...own, color: 'brand' as const, dashed: false }] : [];
    }
    const unallocated = full.chartSeries.find((s) => s.id === 'unallocated');
    const balance = full.chartSeries.find((s) => s.id === 'balance');
    // The unallocated line is the account forecast's own (P:5360); the bank balance rides along
    // only where it shares its currency.
    return [
      ...(unallocated !== undefined
        ? [{ ...unallocated, color: 'brand' as const, dashed: false }]
        : []),
      ...(balance !== undefined && !foreign
        ? [{ ...balance, color: 'text2' as const, dashed: true }]
        : []),
    ];
  }, [full, foreign, cash]);

  if (status === 'loading' && full === null) {
    return (
      <Stack spacing={2.5}>
        <Skeleton variant="rectangular" height={320} />
        <Skeleton variant="rectangular" height={160} />
      </Stack>
    );
  }
  if (status === 'error' && full === null) {
    return (
      <ErrorState
        variant="section"
        error={error}
        what={t('errors.what.cashflow')}
        onRetry={() => void reload()}
      />
    );
  }
  if (full === null) return null;

  const list = table ?? full;
  const startMinor =
    full.chartSeries.find((s) => s.id === (cash ? 'unallocated' : 'balance'))?.points[0]
      ?.balanceMinor ?? null;
  const endMinor = cash ? full.endUnallocatedMinor : full.endMinor;
  // The currency of the KPIs and the chart: Unallocated is booked in the operating currency (MM-D-63), a
  // balance in the account's own.
  const figureCurrency = cash ? operating : bank;
  const delta = startMinor !== null && endMinor !== null ? endMinor - startMinor : null;
  const lowest = full.minimum;
  const cause = lowest?.cause?.payee ?? null;

  const kpis: ForecastKpi[] = [
    {
      id: 'today',
      label: t(cash ? 'forecast.unallocatedToday' : 'forecast.balanceToday'),
      value: <MoneyText minor={startMinor} currency={figureCurrency} variant="figureSm" />,
    },
    {
      id: 'projected',
      label: t('forecast.projected'),
      value: <MoneyText minor={endMinor} currency={figureCurrency} variant="figureSm" />,
      ...(delta !== null
        ? {
            note: (
              <Typography
                variant="caption"
                component="span"
                data-delta=""
                sx={{ color: delta < 0 ? tokens.moneyNegative : tokens.moneyPositive }}
              >
                {t('forecast.overHorizon', {
                  delta: formatMoneyMinor(delta, figureCurrency, locale, true),
                })}
              </Typography>
            ),
          }
        : {}),
    },
    ...(lowest !== null
      ? [
          {
            id: 'lowest',
            label: t('forecast.lowestPoint'),
            value: <MoneyText minor={lowest.balanceMinor} currency={bank} variant="figureSm" />,
            note:
              cause !== null
                ? t('forecast.lowestCause', {
                    date: formatCivilDate(lowest.date, locale),
                    payee: cause,
                  })
                : formatCivilDate(lowest.date, locale),
          },
        ]
      : []),
  ];

  // The envelope filter narrows the events list to those with a slice for that envelope.
  const sliceKeys = new Set(
    list.register
      .filter((row) => row.kind === 'SLICE' && row.envelopeId === envelopeId)
      .map((row) => `${row.seriesId ?? ''}|${row.occursOn ?? row.date}`),
  );
  const rows =
    envelopeId > 0
      ? list.events.filter((event) => sliceKeys.has(`${event.seriesId ?? ''}|${event.occursOn}`))
      : list.events;

  function openEvent(event: EventView) {
    if (event.seriesId === null) return;
    const originalOn =
      list.register.find(
        (row) =>
          row.kind === 'OCCURRENCE' &&
          row.seriesId === event.seriesId &&
          row.occursOn === event.occursOn,
      )?.originalOn ?? event.occursOn;
    void host.openOccurrence(event.seriesId, originalOn);
  }

  const filter = (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel id="account-envelope">{t('cashflow.filterEnvelope')}</InputLabel>
      <Select
        labelId="account-envelope"
        label={t('cashflow.filterEnvelope')}
        value={envelopeId > 0 ? String(envelopeId) : 'all'}
        onChange={(event) =>
          onEnvelopeChange(event.target.value === 'all' ? 0 : Number(event.target.value))
        }
      >
        <MenuItem value="all">{t('cashflow.allEnvelopes')}</MenuItem>
        {envelopes.map((envelope) => (
          <MenuItem key={envelope.id} value={String(envelope.id)}>
            {t('cashflow.envelopeOption', {
              envelope: envelope.name,
              virtualAccount: envelope.virtualAccountName,
            })}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      <ForecastCard
        label={t('accounts.tabCashflow')}
        fromToday={fromToday}
        from={full.from}
        to={full.to}
        chart={{
          series: chartSeries,
          events: full.events,
          register: full.register,
          currency: figureCurrency,
          // The lowest point is a KPI; the plotted line is the unallocated one.
          minimum: null,
          onDrill: setDrill,
        }}
        kpis={kpis}
        note={t(cash ? 'forecast.accountNote' : 'forecast.balanceNote')}
        filter={filter}
        events={{
          rows,
          amountCurrency: bank,
          operatingCurrency: operating,
          ...(cash ? {} : { balanceView: true, startLabel: t('forecast.balanceToday') }),
          startMinor,
          startPending: account.openingPending,
          drilled: drill !== null,
          onWiden: () => setDrill(null),
          ...(owner ? { onOpen: openEvent } : {}),
        }}
      />
      {host.node}
    </>
  );
}
